import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './authApi';

// Gift vouchers at the POS (apex_rails docs/features/gift-vouchers/overview.md §4).
// Codes are always masked in responses; full codes/PINs only exist in emails.
export const giftVoucherApi = createApi({
  reducerPath: 'giftVoucherApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['GiftVoucherSale'],
  endpoints: (builder) => ({

    // GET /gift_voucher_types — sellable types at this store
    getGiftVoucherTypes: builder.query({
      query: () => ({ url: '/gift_voucher_types', method: 'GET' }),
    }),

    // GET /gift_voucher_sales?q=&status=&page= — this store's sales, drafts never listed
    getGiftVoucherSales: builder.query({
      query: (params = {}) => ({
        url: '/gift_voucher_sales',
        method: 'GET',
        params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
      }),
      providesTags: [{ type: 'GiftVoucherSale', id: 'LIST' }],
    }),

    // GET /gift_voucher_sales/:id
    getGiftVoucherSale: builder.query({
      query: (id) => `/gift_voucher_sales/${id}`,
      providesTags: (result, error, id) => [{ type: 'GiftVoucherSale', id }],
    }),

    // POST /gift_voucher_sales — create a draft. Body: { gift_voucher_sale: { ... } }
    createGiftVoucherSale: builder.mutation({
      query: (body) => ({ url: '/gift_voucher_sales', method: 'POST', body }),
    }),

    // PATCH /gift_voucher_sales/:id — sending `lines` replaces all lines
    updateGiftVoucherSale: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/gift_voucher_sales/${id}`, method: 'PATCH', body }),
    }),

    // POST /gift_voucher_sales/:id/recipients_csv — multipart file + gift_voucher_type_id
    uploadGiftVoucherRecipientsCsv: builder.mutation({
      query: ({ id, formData }) => ({ url: `/gift_voucher_sales/${id}/recipients_csv`, method: 'POST', body: formData }),
    }),

    // POST /gift_voucher_sales/:id/approval_requests — { kind, reason, voucher_ids? }
    requestGiftVoucherApproval: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/gift_voucher_sales/${id}/approval_requests`, method: 'POST', body }),
    }),

    // POST /gift_voucher_sales/:id/pay — { payments: [{ payment_mode_id, amount, reference_number }] }
    payGiftVoucherSale: builder.mutation({
      query: ({ id, payments }) => ({ url: `/gift_voucher_sales/${id}/pay`, method: 'POST', body: { payments } }),
      invalidatesTags: (result, error, { id }) => [{ type: 'GiftVoucherSale', id: 'LIST' }, { type: 'GiftVoucherSale', id }],
    }),


    // POST /gift_voucher_sales/:id/assign_recipients — { reason, recipients: [{ name, email, phone, amount }] }
    assignGiftVoucherRecipients: builder.mutation({
      query: ({ id, body }) => ({ url: `/gift_voucher_sales/${id}/assign_recipients`, method: 'POST', body }),
      invalidatesTags: (result, error, { id }) => [{ type: 'GiftVoucherSale', id }],
    }),

    // POST /gift_voucher_sales/:id/cancellations — { voucher_ids?, reason, approval_request_id }
    cancelGiftVoucherSale: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/gift_voucher_sales/${id}/cancellations`, method: 'POST', body }),
      invalidatesTags: (result, error, { id }) => [{ type: 'GiftVoucherSale', id: 'LIST' }, { type: 'GiftVoucherSale', id }],
    }),

    // POST /gift_vouchers/lookup — { code, pin?, order_id? } (30/min per terminal)
    lookupGiftVoucher: builder.mutation({
      query: (body) => ({ url: '/gift_vouchers/lookup', method: 'POST', body }),
    }),

  }),
});

export const {
  useGetGiftVoucherTypesQuery,
  useGetGiftVoucherSalesQuery,
  useGetGiftVoucherSaleQuery,
  useCreateGiftVoucherSaleMutation,
  useUpdateGiftVoucherSaleMutation,
  useUploadGiftVoucherRecipientsCsvMutation,
  useRequestGiftVoucherApprovalMutation,
  usePayGiftVoucherSaleMutation,
  useAssignGiftVoucherRecipientsMutation,
  useCancelGiftVoucherSaleMutation,
  useLookupGiftVoucherMutation,
} = giftVoucherApi;
